import { useDrag } from '@brandfolder/react';
import classNames from 'classnames';
import {
  FunctionComponent,
  HTMLAttributes,
  MutableRefObject,
  ReactNode,
  useContext,
  useEffect,
  useRef
} from 'react';
import styled from 'styled-components';

import { DragAndDropContext } from '@context/drag-and-drop';
import { PermissionLevelContext } from '@context/permission-level';
import { PreviewContext } from '@context/preview';
import { PublishContext } from '@context/publish';
import { ThemeContext } from '@context/theme';
import { BlockDragAndDropFormats } from '@enums/blocks';
import { Block, BlockReorderItem } from '@typings/block';

interface BlockEditorDragProps extends HTMLAttributes<HTMLDivElement> {
  block: Block;
  children: ReactNode;
  dragImageRef?: MutableRefObject<HTMLDivElement | null>;
  edit?: boolean;
  sectionHeading?: boolean;
}

const UnstyledBlockEditorDrag: FunctionComponent<BlockEditorDragProps> = (props) => {
  const { block, children, dragImageRef, edit, sectionHeading, ...otherProps } = props;
  const { key } = block;

  const { setReordering } = useContext(DragAndDropContext);
  const { editable } = useContext(PermissionLevelContext);
  const { preview } = useContext(PreviewContext);
  const { publishing } = useContext(PublishContext);
  const { theme } = useContext(ThemeContext);

  const ref = useRef<HTMLDivElement | null>(null);

  const getData = (): BlockReorderItem => {
    return {
      key
    };
  };

  const disabled = edit || publishing || sectionHeading;

  const { dragging } = useDrag({
    data: getData(),
    dragImageRef,
    disabled,
    format: BlockDragAndDropFormats.BlockReorder,
    ref
  });

  useEffect(() => {
    if (dragging) {
      setReordering(block);
    } else {
      setReordering(null);
    }
  }, [dragging]); // eslint-disable-line react-hooks/exhaustive-deps

  const editing = editable && !preview;

  if (!editing) return null;

  return (
    <div
      {...otherProps}
      className={classNames({
        'block-editor-drag': true,
        'block-editor-drag--dragging': dragging,
        [`${otherProps?.className}`]: !!otherProps?.className
      })}
      ref={ref}
      style={{
        backgroundColor: theme.body.backgroundColor
      }}
    >
      {children}
    </div>
  );
};

const StyledBlockEditorDrag = styled(UnstyledBlockEditorDrag)`
  &.block-editor-drag--dragging {
    cursor: grabbing;
    opacity: 0.5;
  }
`;

export const BlockEditorDrag: FunctionComponent<BlockEditorDragProps> = (props) => {
  return <StyledBlockEditorDrag {...props} />;
};
